import type { ReactNode } from "react";
import type { AccountUser } from "../auth/accountTypes";
import { useSearch } from "./search/SearchProvider";
import { isDiscoveryWorkspaceEnabled } from "../config/featureFlags";
import { discoveryNavigationTarget } from "./personal-apps/discovery/discoveryUrl";
import type { DiscoveryUrlState } from "./personal-apps/discovery/types";
import type { HomeView } from "./personal-apps/homeView";
import SearchProvider from "./search/SearchProvider";
import GlobalCommandPalette from "./search/GlobalCommandPalette";
import MyAppsLauncher from "./personal-apps/MyAppsLauncher";
import GalleryHeader from "./shell/GalleryHeader";
import GlobalFooter from "./GlobalFooter";
import SettingsLayout from "./settings/SettingsLayout";

interface AccountShellProps {
  discoveryState?: DiscoveryUrlState | null;
  homeView?: HomeView;
  route: string;
  onNavigate: (
    route: string,
    opts?: {
      source?: string;
      runId?: string;
      pick?: boolean;
      replace?: boolean;
      launchIntent?: { target: string; ref?: string };
    },
  ) => void;
  user?: AccountUser | null;
  /** Remove the main-content padding (e.g. for a full-bleed app frame). */
  mainBleed?: boolean;
  /** Sign-in entry point for the gallery header's signed-out state. Falls
   *  back to navigating to /settings when absent. */
  onSignIn?: () => void;
  /** Sign-out entry point for the gallery header's avatar menu. */
  onSignOut?: () => void;
  /** Hide the shared site footer on Personal Apps workspaces. Header is shared across routes. */
  hideFooter?: boolean;
  children: ReactNode;
}

export default function AccountShell({
  route,
  discoveryState,
  homeView,
  onNavigate,
  user,
  mainBleed,
  onSignIn,
  onSignOut,
  hideFooter = false,
  children,
}: AccountShellProps) {
  const settingsActive =
    route === "/settings" || route.startsWith("/settings/");
  const content = settingsActive ? (
    <SettingsLayout key={user?.id ?? "signed-out"} route={route}>
      {children}
    </SettingsLayout>
  ) : (
    children
  );

  // Gallery shell (DESIGN.md §GalleryAccountShell): sticky top header, no
  // sidebar; Discover search state + palette and the My Apps launcher are
  // each provided once here. ⌘K/Ctrl+K searches My Apps and Discover;
  // the inline Discover filter opens via
  // "/" or the Discover tab. The former `/search` screen (and
  // its discovery sidebar) is retired.
  const scrollHeaderScreen = route !== "/" || discoveryState?.selection
    ? null
    : homeView === "my-apps" ? "library"
    : isDiscoveryWorkspaceEnabled() && discoveryState ? "discover" : null;
  const galleryShellClassName = [
    "app-shell",
    "app-shell--gallery",
    settingsActive ? "app-shell--settings" : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <SearchProvider
      navigationQuery={
        isDiscoveryWorkspaceEnabled()
          ? (discoveryState?.query ?? "")
          : undefined
      }
      onNavigationQuery={
        isDiscoveryWorkspaceEnabled()
          ? (query) =>
              onNavigate(
                discoveryNavigationTarget({
                  selection: discoveryState?.selection ?? null,
                  themeId: discoveryState?.themeId ?? null,
                  query,
                  sort: discoveryState?.sort,
                }),
                { replace: route === "/" },
              )
          : undefined
      }
    >
      <div className={galleryShellClassName}>
        <SharedHeader
          // Discover is active only on its Grid and Library only on its list;
          // a Preview (`/?c=`) belongs to neither, wherever it was opened from.
          // Studio owns `/studio` and its legacy `/creator` alias.
          activeSection={
            route === "/studio" || route === "/creator"
              ? "studio"
              : discoveryState?.selection
                ? null
                : discoveryState
                  ? "discover"
                  : route === "/"
                    ? "library"
                    : null
          }
          scrollHeaderScreen={scrollHeaderScreen}
          onNavigate={onNavigate}
          user={user}
          onSignIn={onSignIn}
          onSignOut={onSignOut}
        />
        <main
          className={`main-content ${mainBleed ? "main-content--bleed" : ""}`}
        >
          <div className="main-content__body">{content}</div>
          {hideFooter ? null : <GlobalFooter />}
        </main>
        {isDiscoveryWorkspaceEnabled() ? null : (
          <GlobalCommandPalette
            route={route}
            onNavigate={onNavigate}
            signedIn={Boolean(user)}
          />
        )}
        {/* ⌘K has one owner: the header's app search when the Discover
            workspace is on, otherwise this launcher. */}
        {isDiscoveryWorkspaceEnabled() ? null : <MyAppsLauncher signedIn={Boolean(user)} onNavigate={onNavigate} />}
      </div>
    </SearchProvider>
  );
}

function SharedHeader(props: React.ComponentProps<typeof GalleryHeader>) {
  const search = useSearch();
  return (
    <GalleryHeader
      {...props}
      search={isDiscoveryWorkspaceEnabled() ? search : undefined}
    />
  );
}
